import React, { useEffect, useMemo, useState } from 'react';
import { api, type RemovalRequest, formatDate, statusTone } from '../lib/api';

type SendResult = { ok: boolean; attempted: number; sent: number; failed: number; skipped: number; message: string };

const Requests: React.FC = () => {
  const [requests, setRequests] = useState<RemovalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [message, setMessage] = useState('');

  async function load() {
    setLoading(true);
    const rows = await api<RemovalRequest[]>('/api/requests').catch(() => []);
    setRequests(rows || []);
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const emailReady = requests.filter((r) => r.recipient_email && /waiting|approval|queued|draft|email provider setup/i.test(r.status));
    const manual = requests.filter((r) => /manual|webform/i.test(`${r.status} ${r.submission_method}`));
    const sent = requests.filter((r) => /sent|submitted/i.test(r.status));
    const needsFix = requests.filter((r) => /failed|error|required/i.test(`${r.status} ${r.error_message || ''}`));
    return { emailReady, manual, sent, needsFix };
  }, [requests]);

  async function clearUnsent() {
    if (!window.confirm('Clear all unsent drafts and manual tasks? Sent history will be kept.')) return;
    setBusy(true);
    setMessage('Clearing unsent drafts...');
    try {
      const result = await api<{ message: string }>('/api/requests/clear-unsent', { method: 'POST' });
      setMessage(result.message || 'Unsent drafts cleared.');
      await load();
    } catch (err: any) {
      setMessage(err?.message || 'Clear failed.');
    } finally {
      setBusy(false);
    }
  }

  async function sendPending() {
    setBusy(true);
    setMessage('Sending email-ready drafts...');
    try {
      const result = await api<SendResult>('/api/requests/send-pending', { method: 'POST', body: JSON.stringify({ limit: 10 }) });
      setMessage(result.message || `Sent ${result.sent} request(s).`);
      await load();
    } catch (err: any) {
      setMessage(err?.message || 'Send failed. Check email setup and sender domain.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="panelCard">Loading drafts...</div>;

  return (
    <div className="pageStack">
      <section className="heroPanel compactHero">
        <div>
          <span className="eyebrow">Drafts</span>
          <h1>Verified-source drafts and manual tasks.</h1>
          <p>Review evidence-backed drafts here. If sending is blocked, use Copy text or finish webforms manually.</p>
        </div>
        <div className="heroActions">
          <button className="primaryButton" onClick={sendPending} disabled={busy || grouped.emailReady.length === 0}>{busy ? 'Sending...' : 'Send email-ready drafts'}</button>
          <button className="secondaryButton" onClick={load} disabled={busy}>Refresh</button>
          <button className="ghostButton" onClick={clearUnsent} disabled={busy || requests.length === 0}>Clear unsent</button>
        </div>
      </section>

      {message && <div className={`notice ${/fail|error|blocked|required/i.test(message) ? 'danger' : 'good'}`}>{message}</div>}

      <section className="metricsGrid">
        <article className="metricCard"><span>Email-ready</span><strong>{grouped.emailReady.length}</strong><p>Only sends after sender/domain setup.</p></article>
        <article className="metricCard"><span>Manual forms</span><strong>{grouped.manual.length}</strong><p>Open page, copy text, submit manually.</p></article>
        <article className="metricCard"><span>Sent</span><strong>{grouped.sent.length}</strong><p>Already contacted.</p></article>
        <article className="metricCard"><span>Needs fix</span><strong>{grouped.needsFix.length}</strong><p>Blocked or provider issue.</p></article>
      </section>

      <section className="panelCard">
        <div className="sectionHeader">
          <div><span className="eyebrow">Removal queue</span><h2>All broker drafts</h2></div>
          <span className="badge neutral">{requests.length} total</span>
        </div>

        <div className="draftList">
          {requests.length === 0 ? (
            <div className="emptyState">No drafts yet. Go to Index, run “Index + draft,” then return here.</div>
          ) : requests.map((request) => {
            const isOpen = expanded === request.id;
            return (
              <article className="draftCard" key={request.id}>
                <button className="draftSummary" type="button" onClick={() => setExpanded(isOpen ? null : request.id)}>
                  <div>
                    <strong>{request.broker_name || request.subject || 'Broker removal request'}</strong>
                    <p>{request.submission_method} · {request.recipient_email || request.source_url || 'Manual task'} · Follow-up {formatDate(request.follow_up_at)}</p>
                  </div>
                  <span className={`badge ${statusTone(request.status)}`}>{request.status}</span>
                </button>
                {isOpen && (
                  <div className="draftBody">
                    {request.error_message && <div className="notice danger">{request.error_message}</div>}
                    {request.source_url && <a href={request.source_url} target="_blank" rel="noreferrer" className="inlineLink">Open opt-out page</a>}
                    {request.recipient_email && <div className="copyLine"><span>To</span><strong>{request.recipient_email}</strong></div>}
                    <label>
                      Generated request text
                      <textarea readOnly value={request.generated_message || 'No generated message stored.'} />
                    </label>
                    <div className="draftActions">
                      <button className="secondaryButton" onClick={() => navigator.clipboard?.writeText(request.generated_message || '')}>Copy text</button>
                      {request.recipient_email && <button className="secondaryButton" onClick={() => navigator.clipboard?.writeText(request.recipient_email || '')}>Copy email</button>}
                      {request.source_url && <button className="ghostButton" onClick={() => window.open(request.source_url, '_blank')}>Open form</button>}
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default Requests;
